import axios from "axios";

const API_URL = "https://69455dfbed253f51719b2366.mockapi.io/api/v1/todos";
const STORAGE_KEY = "todo-app-ts.todos";
const USE_REMOTE_API = false;
const normalizeId = (id: string | number) => String(id);

const readLocalTodos = (): TodoType[] => {
  const rawTodos = localStorage.getItem(STORAGE_KEY);

  if (!rawTodos) {
    return [];
  }

  try {
    return JSON.parse(rawTodos) as TodoType[];
  } catch {
    return [];
  }
};

const writeLocalTodos = (todos: TodoType[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(todos));
};

const createLocalTodo = (
  task: string,
  priority: "low" | "medium" | "high"
): TodoType => ({
  id: crypto.randomUUID(),
  task,
  isDone: false,
  priority,
});

const createRemoteTodoPayload = (
  task: string,
  priority: "low" | "medium" | "high"
) => ({
  task,
  isDone: false,
  priority,
});

export const todoService = {
  getAllTodos: async (): Promise<TodoType[]> => {
    if (!USE_REMOTE_API) {
      return readLocalTodos();
    }

    try {
      const { data } = await axios.get<TodoType[]>(API_URL);
      writeLocalTodos(data);
      return data;
    } catch {
      return readLocalTodos();
    }
  },

  addTodo: async (
    task: string,
    priority: "low" | "medium" | "high"
  ): Promise<TodoType> => {
    const newTodo = createLocalTodo(task, priority);

    if (!USE_REMOTE_API) {
      const currentTodos = readLocalTodos();
      writeLocalTodos([...currentTodos, newTodo]);
      return newTodo;
    }

    try {
      const { data } = await axios.post<TodoType>(
        API_URL,
        createRemoteTodoPayload(task, priority)
      );
      const currentTodos = readLocalTodos();
      writeLocalTodos([...currentTodos, data]);
      return data;
    } catch {
      const currentTodos = readLocalTodos();
      writeLocalTodos([...currentTodos, newTodo]);
      return newTodo;
    }
  },

  updateTodo: async (
    id: string | number,
    updatedTodo: Partial<TodoType>
  ): Promise<TodoType> => {
    if (!USE_REMOTE_API) {
      const currentTodos = readLocalTodos();
      let updatedItem: TodoType | undefined;

      const nextTodos = currentTodos.map((todo) => {
        if (normalizeId(todo.id) !== normalizeId(id)) {
          return todo;
        }

        updatedItem = { ...todo, ...updatedTodo };
        return updatedItem;
      });

      writeLocalTodos(nextTodos);
      return updatedItem as TodoType;
    }

    try {
      const { data } = await axios.put<TodoType>(`${API_URL}/${id}`, updatedTodo);
      const currentTodos = readLocalTodos();
      const nextTodos = currentTodos.map((todo) =>
        normalizeId(todo.id) === normalizeId(id) ? { ...todo, ...data } : todo
      );
      writeLocalTodos(nextTodos);
      return data;
    } catch {
      const currentTodos = readLocalTodos();
      let updatedItem: TodoType | undefined;

      const nextTodos = currentTodos.map((todo) => {
        if (normalizeId(todo.id) !== normalizeId(id)) {
          return todo;
        }

        updatedItem = { ...todo, ...updatedTodo };
        return updatedItem;
      });

      writeLocalTodos(nextTodos);
      return updatedItem as TodoType;
    }
  },

  deleteTodo: async (id: string | number): Promise<void> => {
    if (!USE_REMOTE_API) {
      const currentTodos = readLocalTodos();
      writeLocalTodos(
        currentTodos.filter((todo) => normalizeId(todo.id) !== normalizeId(id))
      );
      return;
    }

    try {
      await axios.delete(`${API_URL}/${id}`);
    } finally {
      const currentTodos = readLocalTodos();
      writeLocalTodos(
        currentTodos.filter((todo) => normalizeId(todo.id) !== normalizeId(id))
      );
    }
  },

  deleteMultipleTodos: async (ids: (string | number)[]): Promise<void> => {
    if (!USE_REMOTE_API) {
      const idSet = new Set(ids.map(normalizeId));
      const currentTodos = readLocalTodos();
      writeLocalTodos(
        currentTodos.filter((todo) => !idSet.has(normalizeId(todo.id)))
      );
      return;
    }

    try {
      await Promise.all(ids.map((id) => axios.delete(`${API_URL}/${id}`)));
    } finally {
      const idSet = new Set(ids.map(normalizeId));
      const currentTodos = readLocalTodos();
      writeLocalTodos(
        currentTodos.filter((todo) => !idSet.has(normalizeId(todo.id)))
      );
    }
  },
};
